import React from "react";
import { Link } from "react-router-dom";
import { Heart, ShoppingCart, Star } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { formatRp, imageUrl } from "@/lib/api";

export default function ProductCard({ product, compact = false }) {
  const { addToCart, toggleWishlist, isWishlisted } = useCart();
  const price = product.sale_price || product.price;
  const hasDiscount = !!product.sale_price && product.sale_price < product.price;
  const discountPct = hasDiscount
    ? Math.round(((product.price - product.sale_price) / product.price) * 100)
    : 0;
  const wished = isWishlisted(product.id);

  return (
    <div
      data-testid={`product-card-${product.slug}`}
      className="group bg-white rounded-2xl border border-[#E5E5E5] overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all duration-300 relative flex flex-col"
    >
      <Link to={`/produk/${product.slug}`} className="block relative aspect-square bg-[#FAF9F6] overflow-hidden">
        {hasDiscount && (
          <span className="absolute top-2 left-2 brand-bg-primary text-white text-[10px] font-bold px-2 py-1 rounded-lg z-10">
            -{discountPct}%
          </span>
        )}
        {product.is_bestseller && (
          <span className="absolute top-2 right-2 bg-white/95 brand-text-primary text-[10px] font-bold px-2 py-1 rounded-lg z-10 border brand-border-primary">
            ⭐ Terlaris
          </span>
        )}
        {product.images?.[0] ? (
          <img
            src={imageUrl(product.images[0])}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full grid place-items-center text-[#E5E5E5]">No image</div>
        )}
        <button
          onClick={(e) => {
            e.preventDefault();
            toggleWishlist(product.id);
          }}
          data-testid={`wishlist-btn-${product.slug}`}
          className={`absolute bottom-2 right-2 w-8 h-8 rounded-full grid place-items-center backdrop-blur-md transition-colors ${
            wished ? "brand-bg-primary text-white" : "bg-white/90 text-[#1A1A1A] hover:bg-white"
          }`}
        >
          <Heart size={14} fill={wished ? "white" : "none"} />
        </button>
      </Link>
      <div className="p-3 flex-1 flex flex-col">
        <Link to={`/produk/${product.slug}`} className="block">
          <h3 className="text-sm font-semibold leading-snug line-clamp-2 min-h-[2.5em] text-[#1A1A1A] hover:brand-text-primary transition-colors">
            {product.name}
          </h3>
        </Link>
        <div className="mt-2 flex items-end gap-2">
          <div className="brand-text-primary font-bold text-base">{formatRp(price)}</div>
          {hasDiscount && (
            <div className="text-xs text-[#5C5C5C] line-through pb-0.5">{formatRp(product.price)}</div>
          )}
        </div>
        <div className="mt-1.5 flex items-center gap-2 text-[11px] text-[#5C5C5C]">
          {product.rating_avg > 0 && (
            <span className="flex items-center gap-0.5">
              <Star size={11} fill="#FFB703" stroke="#FFB703" />
              {product.rating_avg.toFixed(1)}
            </span>
          )}
          <span>•</span>
          <span>{product.sold_count || 0} terjual</span>
          <span className="ml-auto text-[10px] uppercase font-bold text-[#5C5C5C]">/{product.unit}</span>
        </div>
        {!compact && (
          <button
            onClick={(e) => {
              e.preventDefault();
              addToCart(product.id, 1);
            }}
            data-testid={`add-to-cart-btn-${product.slug}`}
            disabled={product.stock <= 0}
            className="mt-3 w-full flex items-center justify-center gap-1.5 brand-soft-bg brand-text-primary hover:brand-bg-primary hover:text-white font-bold text-xs py-2 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ShoppingCart size={14} />
            {product.stock <= 0 ? "Habis" : "Tambah"}
          </button>
        )}
      </div>
    </div>
  );
}
